// The "is this setup step really done?" reads behind <FirstRunChecklist/>.
//
// D1 (F19 setup flow): the Home setup list ticked steps that were not done on a
// brand-new business's first day —
//   * identity, because provisioning creates the packhouse_config row it
//     counted (provision-packhouse-tenant upserts it);
//   * target markets, because every business reads the shared baseline of
//     every ISO country (mig 20260630130000 + 20260701230000);
//   * "Invite your team", for any admin who belongs to a second business,
//     because user_tenants_self_read returns the caller's memberships in
//     EVERY business;
// and a read that failed came back as 0 (`if (error) return 0`).
//
// So the three false ticks read the real thing, scoped to the active tenant by
// id (RLS alone shows the shared baseline / the caller's other memberships),
// and EVERY check throws on a failed read. The checklist renders a thrown
// check as "Couldn't check", never as done and never as zero.
//
// The registry counts (rates, customers, transporters, exporters, lanes) are
// scoped by id too: in platform view a platform admin's RLS reads every
// business's rows, so an RLS-only count ticked them on a brand-new business.
// Growers alone keep their RLS scope — see the note on that row.

import { supabase } from '@/lib/supabase'
import { countOrThrow } from '@/lib/readState'
import { fetchAllPaged } from '@/lib/fetchAllPaged'

export type StepState = 'done' | 'open' | 'unknown' | 'loading'

export const SETUP_CHECKS = [
  'identity', 'rates', 'growers', 'customers', 'transporters',
  'exporters', 'markets', 'lanes', 'members',
  // UF-02: the steps a first day really needs. The three cooling ones apply
  // only to a force-cool (berry) flow — FORCE_COOL_CHECKS.
  'fruit', 'presets', 'coldrooms', 'printing',
  'coolingRooms', 'coolingDefaults', 'cartonUnit',
] as const
export type SetupCheckKey = typeof SETUP_CHECKS[number]

/** Read (and shown) only on a business whose flow force-cools (berries). */
export const FORCE_COOL_CHECKS: readonly SetupCheckKey[] = ['coolingRooms', 'coolingDefaults', 'cartonUnit']

export interface SetupCheckResult {
  done: boolean
  /** identity only — the two fields it is made of. */
  legalNameSet?: boolean
  phcSet?: boolean
  /** rates only (RB1 D6) — how many of the visible active pack formats have an
   *  ACTIVE rate card with the same code. Absent when it could not be read. */
  formats?: { total: number; withCard: number }
}

const filled = (v: unknown): boolean => typeof v === 'string' && v.trim() !== ''

/** A head count. With `tenantId`, only rows this business OWNS. */
async function count(table: string, tenantId?: string): Promise<number> {
  const q = supabase.from(table).select('*', { count: 'exact', head: true })
  return countOrThrow(await (tenantId ? q.eq('tenant_id', tenantId) : q))
}

/**
 * One step's verdict. Throws when the read fails — the caller must show that
 * as "couldn't check", never fold it into a zero.
 *
 * `identity.done` means the legal name is set; whether the PHC is ALSO needed
 * depends on the commodity flow (only a PPECB business carries one), which the
 * component knows and this read does not — it returns both halves.
 */
export async function runSetupCheck(key: SetupCheckKey, tenantId: string): Promise<SetupCheckResult> {
  return CHECKS[key](tenantId)
}

const done = (n: number): SetupCheckResult => ({ done: n > 0 })
/** A head count of rows this business OWNS that also pass `narrow`. */
const ownedWhere = (table: string, narrow: (q: CountQuery) => CountQuery) =>
  async (tenantId: string): Promise<SetupCheckResult> =>
    done(countOrThrow(await narrow(supabase.from(table)
      .select('*', { count: 'exact', head: true }).eq('tenant_id', tenantId) as unknown as CountQuery)))
/** The filters a step narrows its head count with — just enough of the
 *  PostgREST builder (its full generic type is too deep to name here). */
interface CountQuery extends PromiseLike<{ count: number | null; error: unknown }> {
  eq(column: string, value: unknown): CountQuery
  neq(column: string, value: unknown): CountQuery
  is(column: string, value: null): CountQuery
}
const registry = (table: string) => async (): Promise<SetupCheckResult> => done(await count(table))
const owned = (table: string) => async (tenantId: string): Promise<SetupCheckResult> =>
  done(await count(table, tenantId))
/** Does this business own at least one row? For a table that grows with
 *  every day's work (one row per printed label), where a head count would
 *  scan a live business's whole history on every Config → Setup open — reads
 *  at most one row, like hasAnyPallet. */
const ownsAny = (table: string, column: string) =>
  async (tenantId: string): Promise<SetupCheckResult> => {
    const { data, error } = await supabase.from(table)
      .select(column).eq('tenant_id', tenantId).limit(1)
    if (error) throw error
    return { done: (data?.length ?? 0) > 0 }
  }

async function identityCheck(tenantId: string): Promise<SetupCheckResult> {
  const { data, error } = await supabase.from('packhouse_config')
    .select('legal_name, phc').eq('tenant_id', tenantId).maybeSingle()
  if (error) throw error
  const row = data as { legal_name: string | null; phc: string | null } | null
  const legalNameSet = filled(row?.legal_name)
  return { done: legalNameSet, legalNameSet, phcSet: filled(row?.phc) }
}

/** Pack format codes with / without an active card, by code (D6). Pure. */
export function packFormatCoverage(formatCodes: readonly string[], cardCodes: readonly string[]):
  { total: number; withCard: number } {
  const cards = new Set(cardCodes)
  const formats = new Set(formatCodes)
  let withCard = 0
  for (const c of formats) if (cards.has(c)) withCard++
  return { total: formats.size, withCard }
}

/** "Add pack types & rate cards": done once this business owns an ACTIVE card
 *  (a retired one proves nothing, D6). The N-of-M figure is decoration, so a
 *  failed read of it leaves it out; it never fails the verdict, never reads 0. */
async function ratesCheck(tenantId: string): Promise<SetupCheckResult> {
  const verdict = await ownedWhere('financial_products', q => q.eq('is_active', true))(tenantId)
  try {
    // Formats = this business's own plus the shared baseline (tenant_id NULL),
    // for the fruit it handles (no fruit ticked = every fruit, as everywhere).
    const [fmt, cards, handled] = await Promise.all([
      fetchAllPaged<{ code: string; commodity_id: string }>((from, to) => supabase.from('commodity_pack_formats')
        .select('code, commodity_id').eq('is_active', true)
        .or(`tenant_id.eq.${tenantId},tenant_id.is.null`).order('format_id').range(from, to)),
      fetchAllPaged<{ product_code: string }>((from, to) => supabase.from('financial_products')
        .select('product_code').eq('tenant_id', tenantId).eq('is_active', true).order('product_id').range(from, to)),
      fetchAllPaged<{ commodity_id: string }>((from, to) => supabase.from('tenant_commodities')
        .select('commodity_id').eq('tenant_id', tenantId).order('commodity_id').range(from, to)),
    ])
    if (fmt.error || cards.error || handled.error) return verdict
    const fruit = new Set(handled.rows.map(r => r.commodity_id))
    const codes = fmt.rows.filter(r => fruit.size === 0 || fruit.has(r.commodity_id)).map(r => r.code)
    return { ...verdict, formats: packFormatCoverage(codes, cards.rows.map(r => r.product_code)) }
  } catch {
    return verdict
  }
}

async function membersCheck(tenantId: string): Promise<SetupCheckResult> {
  // Someone OTHER than me is an active member of THIS business.
  const { data: sess, error: sessErr } = await supabase.auth.getSession()
  if (sessErr) throw sessErr
  const me = sess.session?.user?.id
  if (!me) throw new Error('No signed-in user to compare members against')
  return ownedWhere('user_tenants', q => q.is('revoked_at', null).neq('user_id', me))(tenantId)
}

async function coolingDefaultsCheck(tenantId: string): Promise<SetupCheckResult> {
  const { data, error } = await supabase.from('packhouse_config')
    .select('cooling_defaults').eq('tenant_id', tenantId).maybeSingle()
  if (error) throw error
  return { done: hasCrateWeight((data as { cooling_defaults: unknown } | null)?.cooling_defaults) }
}

const CHECKS: Record<SetupCheckKey, (tenantId: string) => Promise<SetupCheckResult>> = {
  identity: identityCheck,
  // A business "puts its markets first" by ordering or hiding one, which
  // writes a row it OWNS (copy-on-write over the shared baseline). Baseline
  // rows (tenant_id NULL) say nothing about this business, so scope by id.
  markets:      owned('target_markets'),
  members:      membersCheck,
  // Rows this business OWNS — a platform admin's RLS (platform view) reads
  // every business's, which ticked these on a brand-new one.
  rates:        ratesCheck,
  // Deliberately RLS-scoped (D1): a packhouse also sees the growers of a farm
  // it is linked to (growers_read_scoped, mig 20260710100630 —
  // current_tenant_grower_ids()), and those count as having growers; a
  // tenant_id filter would re-open a step a linked packhouse has really done.
  // The cost: in platform view a platform admin's RLS reads every grower, so
  // this one step can still tick there on a brand-new business.
  growers:      registry('growers'),
  customers:    owned('customers'),
  transporters: owned('transporters'),
  exporters:    owned('exporters'),
  lanes:        owned('loading_bay_lanes'),
  // UF-02 — every one of these is scoped to THIS business by id: a platform
  // admin's RLS reads every tenant's rows, which would tick them all.
  fruit:        owned('tenant_commodities'),
  presets:      ownedWhere('line_presets', q => q.is('deleted_at', null)),
  coldrooms:    ownedWhere('coldrooms', q => q.eq('is_active', true)),
  // A print station is done once a PC of this business has printed a label
  // (every pallet-label print writes a label_prints row stamped with the
  // station's name — lib/labels/printLog.ts).
  printing:     ownsAny('label_prints', 'print_id'),
  // Force-cool flows only (the component decides whether they apply).
  coolingRooms: owned('cooling_stage_rooms'),
  coolingDefaults: coolingDefaultsCheck,
  // The berry label's NET/CARTON reads the commodity's CARTON pack unit.
  cartonUnit:   ownedWhere('commodity_pack_units', q => q.eq('unit_code', 'CARTON').eq('is_active', true)),
}

/** Cooling defaults are "set" once a standard crate weight is saved for at
 *  least one variety — the number the Precool batch weights are built from
 *  (CoolingDefaultsSection writes `std_crate_gross_kg`). A target °C alone is
 *  only a prefill. */
export function hasCrateWeight(cfg: unknown): boolean {
  if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) return false
  const w = (cfg as Record<string, unknown>)['std_crate_gross_kg']
  if (!w || typeof w !== 'object' || Array.isArray(w)) return false
  return Object.values(w).some(v => typeof v === 'number' && Number.isFinite(v) && v > 0)
}

/**
 * Has this business saved a pallet yet? The Home setup card is for a business's
 * first days only (F19 UF-01): once it packs, the card never comes back, even
 * when a later, stricter step reads open. One row is enough to answer, so this
 * reads at most one — a head count would scan a live business's whole pallet
 * history on every Home load. A soft-deleted pallet (a test one, removed)
 * does not count. Throws on a failed read; the card then stays hidden rather
 * than guessing.
 */
export async function hasAnyPallet(tenantId: string): Promise<boolean> {
  const { data, error } = await supabase.from('pallets')
    .select('pallet_id').eq('tenant_id', tenantId).is('deleted_at', null).limit(1)
  if (error) throw error
  return (data?.length ?? 0) > 0
}

/** The identity row's hint — names the field that is still empty. */
export function identityHint(r: SetupCheckResult | undefined, usesPPECB: boolean): string {
  if (r && !r.legalNameSet) return 'Legal name still empty — it prints on every invoice, statement and pallet label.'
  if (r && usesPPECB && !r.phcSet) return 'PHC still empty — it prints on every pallet label and goes on every PPECB submission.'
  return usesPPECB
    ? 'Legal name and PHC — printed on every invoice, statement and pallet label.'
    : 'Legal name — printed on every invoice, statement and pallet label.'
}

// ── "Not needed here" (F19 UF-30) ─────────────────────────────────────────────
// A business that really skips a step (no transporters: its buyers collect)
// marks it once, for every PC: one system_config row per business, key
// `setup_not_needed`, value a JSON array of step keys. No migration —
// system_config is readable by every member and writable by a business ADMIN
// or MANAGER (config_read / config_write / config_write_insert), and its CHECK
// passes any key it does not name. It replaces the old per-PC localStorage
// dismiss as the way to settle a step the business will never do.

export const NOT_NEEDED_KEY = 'setup_not_needed'

/** The step keys in a stored value. Anything that is not a JSON array of
 *  strings reads as none — a bad value never hides a step. */
export function parseNotNeeded(raw: unknown): string[] {
  if (typeof raw !== 'string' || raw.trim() === '') return []
  try {
    const v: unknown = JSON.parse(raw)
    return Array.isArray(v) ? v.filter((k): k is string => typeof k === 'string') : []
  } catch {
    return []
  }
}

/** The value to store for these keys: deduplicated and sorted, so two PCs
 *  writing the same choice write the same text. */
export function notNeededValue(keys: readonly string[]): string {
  return JSON.stringify([...new Set(keys)].sort())
}

/** This business's "not needed here" steps. Throws on a failed read — the
 *  caller then offers no control (a write would replace a list it never saw). */
export async function readNotNeeded(tenantId: string): Promise<string[]> {
  const { data, error } = await supabase.from('system_config')
    .select('config_value').eq('tenant_id', tenantId).eq('config_key', NOT_NEEDED_KEY)
    .maybeSingle()
  if (error) throw error
  return parseNotNeeded((data as { config_value: string } | null)?.config_value)
}
